// --------------------------------------------------------------------------
//  Export builder file generators.
//
//  Consumes the resolved section blocks from exportData.js (which already
//  carry real numbers and rows) and renders them into the four download
//  formats. The same block shapes drive the on-screen preview, so a generated
//  file matches what the customer saw while building it.
//
//    - PDF        : jsPDF, drawn section by section with real tables/metrics.
//    - PowerPoint : self-contained HTML deck, one slide per section, opens as
//                   a presentation and exports to PPTX via the OS print path.
//    - Excel      : a genuine .xlsx workbook built with SheetJS, one sheet per
//                   section, each laid out as plain rows-and-columns. Real
//                   binary OOXML, so Excel opens it with no "format/extension
//                   don't match" warning.
//    - CSV        : labelled data blocks, one per section.
//
//  Large tables export the same four ways (CSV / Excel / JSON / PDF) at full
//  row count, separate from the composed document.
//
//  Postgres mapping: none, pure client rendering. The PDF pipeline is the
//  piece a server-side renderer would eventually replace for pixel-perfect
//  charts; the block contract here is what that service would consume.
// --------------------------------------------------------------------------

import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { escapeHtml, escapeCsv } from './exportData';

// The real OOXML spreadsheet MIME. We hand downloadBlob a Blob already typed
// with this, so the saved .xlsx carries the correct content type and Excel
// opens it without the format/extension warning the old HTML-as-.xls produced.
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Composed documents cap each table so a board report stays readable; the
// full dataset lives in the Large data tables panel. Notes make the cap
// explicit so a truncated table never reads as "this is everything".
const DOC_TABLE_LIMIT = 20;
const todayLabel = () => new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

// ==========================================================================
//  CSV  (multi-section, labelled blocks)
// ==========================================================================
function blockToCsvLines(label, block) {
    const lines = [`## ${label}`];
    if (block.kind === 'gauge') {
        lines.push('metric,value', escapeCsv('Risk score') + ',' + escapeCsv(block.value), escapeCsv('Band') + ',' + escapeCsv(block.band), escapeCsv('Context') + ',' + escapeCsv(block.sub));
    } else if (block.kind === 'metrics') {
        lines.push('metric,value,detail');
        block.items.forEach((m) => lines.push([m.label, m.value, m.sub].map(escapeCsv).join(',')));
    } else if (block.kind === 'bars') {
        lines.push(`label,${escapeCsv(block.unit || 'value')}`);
        block.items.forEach((b) => lines.push([b.label, b.value].map(escapeCsv).join(',')));
    } else if (block.kind === 'table') {
        lines.push(block.columns.map(escapeCsv).join(','));
        block.rows.forEach((r) => lines.push(r.map(escapeCsv).join(',')));
        if (block.total != null && block.total > block.rows.length) lines.push(`# ${block.rows.length} of ${block.total} rows`);
    } else if (block.kind === 'narrative') {
        block.paragraphs.forEach((p) => {
            if (p.h) lines.push(escapeCsv(p.h));
            lines.push(escapeCsv(p.body));
            if (p.action) lines.push(escapeCsv(`Action: ${p.action}`));
        });
    }
    return lines;
}

export function generateCsv(title, sections) {
    const out = [`# ${title}`, `# Generated ${todayLabel()} · Boxphish`, ''];
    sections.forEach((s) => {
        out.push(...blockToCsvLines(s.label, s.block));
        if (s.withDrill) out.push(`# drill-down requested: ${s.drillLabel || 'row-level detail'}`);
        out.push('');
    });
    return out.join('\n');
}

// ==========================================================================
//  Excel  (genuine .xlsx workbook via SheetJS)
// ==========================================================================

// Excel sheet names: max 31 chars, must be non-empty, may not contain : \ / ? * [ ].
function sanitizeSheetName(name) {
    const cleaned = String(name == null ? '' : name).replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim();
    return cleaned.slice(0, 31) || 'Sheet';
}

// Size each column to its widest cell so the sheet opens readable rather than
// with every column clamped to the default width.
function autoFitColumns(aoa) {
    const ncol = aoa.reduce((m, r) => Math.max(m, r.length), 0);
    const cols = [];
    for (let c = 0; c < ncol; c++) {
        let widest = 8;
        for (const row of aoa) {
            const v = row[c];
            if (v != null) widest = Math.max(widest, String(v).length + 2);
        }
        cols.push({ wch: Math.min(widest, 60) });
    }
    return cols;
}

// Build a real .xlsx Blob from [{ name, aoa }] sheets. Sheet names are
// sanitised and de-duplicated so book_append_sheet never throws.
function buildWorkbook(sheets) {
    const wb = XLSX.utils.book_new();
    const used = new Set();
    sheets.forEach((s, idx) => {
        const aoa = s.aoa && s.aoa.length ? s.aoa : [['']];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        ws['!cols'] = autoFitColumns(aoa);
        let name = sanitizeSheetName(s.name || `Sheet ${idx + 1}`);
        let unique = name, n = 2;
        while (used.has(unique.toLowerCase())) { unique = `${name.slice(0, 28)} ${n++}`; }
        used.add(unique.toLowerCase());
        XLSX.utils.book_append_sheet(wb, ws, unique);
    });
    const buf = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    return new Blob([buf], { type: XLSX_MIME });
}

// Turn a resolved block into a plain rows-and-columns array of arrays. Values
// stay exactly as the preview shows them (already formatted), so what the
// customer saw while building the export is what lands in the cells.
function blockToAoa(block) {
    if (block.kind === 'gauge') {
        return [['Metric', 'Value'], ['Risk score', block.value], ['Band', block.band], ['Context', block.sub]];
    }
    if (block.kind === 'metrics') {
        return [['Metric', 'Value', 'Detail'], ...block.items.map((m) => [m.label, m.value, m.sub])];
    }
    if (block.kind === 'bars') {
        return [['Label', block.unit || 'Value'], ...block.items.map((b) => [b.label, b.value])];
    }
    if (block.kind === 'table') {
        return [block.columns, ...block.rows];
    }
    if (block.kind === 'narrative') {
        const aoa = [['Section', 'Detail']];
        block.paragraphs.forEach((p) => {
            aoa.push([p.h || '', p.body]);
            if (p.action) aoa.push(['Action', p.action]);
        });
        return aoa;
    }
    return [['']];
}

export function generateXls(title, sections) {
    // A cover sheet lists the document and its contents; every section then
    // gets its own clean tab so each is a self-contained table.
    const cover = {
        name: 'Report',
        aoa: [
            [title || 'Report'],
            [`Generated ${todayLabel()} · Boxphish`],
            [],
            ['#', 'Section', 'Group'],
            ...sections.map((s, i) => [i + 1, s.label, s.group]),
        ],
    };
    const sheets = sections.map((s, i) => ({ name: `${i + 1}. ${s.label}`, aoa: blockToAoa(s.block) }));
    return buildWorkbook([cover, ...sheets]);
}

// ==========================================================================
//  PowerPoint  (HTML deck)
// ==========================================================================
function blockToSlideHtml(block) {
    if (block.kind === 'gauge') {
        return `<div class="gauge"><div class="big">${block.value}</div><div class="band">${escapeHtml(block.band)}</div><div class="ctx">${escapeHtml(block.sub)}</div></div>`;
    }
    if (block.kind === 'metrics') {
        return `<div class="tiles">${block.items.map((m) => `<div class="tile"><div class="tv">${escapeHtml(m.value)}</div><div class="tl">${escapeHtml(m.label)}</div><div class="ts">${escapeHtml(m.sub)}</div></div>`).join('')}</div>`;
    }
    if (block.kind === 'bars') {
        const max = Math.max(1, ...block.items.map((b) => Number(b.value) || 0));
        return `<div class="bars">${block.items.map((b) => `<div class="bar"><span class="bl">${escapeHtml(b.label)}</span><span class="bt"><span class="bf" style="width:${Math.round(((Number(b.value) || 0) / (b.max || max)) * 100)}%"></span></span><span class="bv">${escapeHtml(b.value)}</span></div>`).join('')}</div>`;
    }
    if (block.kind === 'table') {
        const rows = block.rows.slice(0, 12);
        return `<table class="t"><thead><tr>${block.columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('')}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${escapeHtml(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>${block.total > rows.length ? `<div class="more">Showing ${rows.length} of ${block.total} rows</div>` : ''}`;
    }
    return `<div class="narr">${block.paragraphs.map((p) => `${p.h ? `<h4>${escapeHtml(p.h)}</h4>` : ''}<p>${escapeHtml(p.body)}</p>${p.action ? `<p class="act">→ ${escapeHtml(p.action)}</p>` : ''}`).join('')}</div>`;
}

export function generatePptx(title, sections) {
    const cover = `<section class="slide cover"><h1>${escapeHtml(title)}</h1><p>Boxphish · ${escapeHtml(todayLabel())}</p></section>`;
    const slides = sections.map((s, i) => `
        <section class="slide">
            <div class="meta">Slide ${i + 1} · ${escapeHtml(s.group)}</div>
            <h2>${escapeHtml(s.label)}</h2>
            <div class="body">${blockToSlideHtml(s.block)}</div>
        </section>`).join('');
    return `<!doctype html><html><head><meta charset="UTF-8"><title>${escapeHtml(title)}</title>
<style>
body{font-family:'Segoe UI',system-ui,sans-serif;background:#1f2937;margin:0;padding:32px}
.slide{width:960px;height:540px;background:#fff;margin:0 auto 24px;padding:48px 56px;box-sizing:border-box;box-shadow:0 8px 24px rgba(0,0,0,.3);display:flex;flex-direction:column;overflow:hidden}
.slide.cover{justify-content:center;align-items:center;text-align:center}
.slide.cover h1{font-size:46px;margin:0;color:#0F172A}.slide.cover p{color:#64748B;margin-top:12px}
.meta{font-size:11px;color:#94A3B8;text-transform:uppercase;letter-spacing:.08em}
.slide h2{font-size:26px;color:#0F172A;margin:8px 0 20px}
.body{flex:1;min-height:0}
.gauge{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%}
.gauge .big{font-size:96px;font-weight:800;color:#FF5252;line-height:1}.gauge .band{font-size:20px;color:#0F172A;margin-top:8px}.gauge .ctx{font-size:14px;color:#64748B;margin-top:6px}
.tiles{display:grid;grid-template-columns:1fr 1fr;gap:16px;height:100%}
.tile{border:1px solid #E2E8F0;border-radius:10px;padding:18px;display:flex;flex-direction:column;justify-content:center}
.tile .tv{font-size:40px;font-weight:800;color:#0F172A}.tile .tl{font-size:14px;color:#0F172A;margin-top:4px}.tile .ts{font-size:11px;color:#94A3B8}
.bars{display:flex;flex-direction:column;gap:12px;justify-content:center;height:100%}
.bar{display:flex;align-items:center;gap:12px}.bar .bl{width:120px;font-size:13px;color:#0F172A}.bar .bt{flex:1;height:14px;background:#EEF2F6;border-radius:7px;overflow:hidden}.bar .bf{display:block;height:100%;background:#00BFA5}.bar .bv{width:60px;text-align:right;font-size:13px;font-weight:600;color:#0F172A}
.t{width:100%;border-collapse:collapse;font-size:12px}.t th{text-align:left;color:#64748B;font-size:10px;text-transform:uppercase;letter-spacing:.04em;padding:6px 8px;border-bottom:2px solid #E2E8F0}.t td{padding:6px 8px;border-bottom:1px solid #F1F5F9;color:#334155}
.more{font-size:11px;color:#94A3B8;margin-top:8px}
.narr h4{font-size:15px;color:#0F172A;margin:10px 0 4px}.narr p{font-size:13px;color:#334155;line-height:1.5;margin:0 0 8px}.narr .act{color:#00897B;font-style:italic}
@media print{body{background:#fff;padding:0}.slide{box-shadow:none;page-break-after:always}}
</style></head><body>${cover}${slides}</body></html>`;
}

// ==========================================================================
//  PDF  (jsPDF, real data drawn block by block)
// ==========================================================================
export function generatePdf(title, sections) {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 48;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usable = pageWidth - margin * 2;
    let y = margin;

    const ensure = (needed) => { if (y > pageHeight - margin - needed) { doc.addPage(); y = margin; } };
    const setText = (size, style, shade) => { doc.setFont('helvetica', style); doc.setFontSize(size); doc.setTextColor(shade); };

    // Cover header
    setText(22, 'bold', 20);
    const titleLines = doc.splitTextToSize(title || 'Untitled report', usable);
    doc.text(titleLines, margin, y);
    y += 20 + (titleLines.length - 1) * 24;
    setText(9, 'normal', 130);
    doc.text(`Boxphish · ${todayLabel()} · ${sections.length} section${sections.length === 1 ? '' : 's'}`, margin, y);
    y += 10;
    doc.setDrawColor(30); doc.setLineWidth(1); doc.line(margin, y, pageWidth - margin, y);
    y += 26;

    sections.forEach((s, i) => {
        ensure(60);
        setText(8, 'bold', 150);
        doc.text(`SECTION ${i + 1} · ${String(s.group).toUpperCase()}${s.withDrill ? ' · WITH DRILL-DOWN' : ''}`, margin, y);
        y += 13;
        setText(13, 'bold', 20);
        doc.text(s.label, margin, y);
        y += 16;
        drawBlock(doc, s.block, { margin, usable, pageHeight, ensure, setText, getY: () => y, setY: (v) => { y = v; } });
        y += 18;
    });

    setText(8, 'normal', 150);
    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.text(`Page ${p} of ${pageCount}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
        doc.text('Confidential', margin, pageHeight - 20);
    }
    return doc.output('blob');
}

// Truncate the given text to fit maxWidth (pt) in the doc's current font, adding
// an ellipsis so clipped table cells / bar labels read as intentional, not cut off.
function clipText(doc, text, maxWidth) {
    const s = String(text ?? '');
    if (maxWidth <= 0 || doc.getTextWidth(s) <= maxWidth) return s;
    let lo = 0, hi = s.length;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (doc.getTextWidth(s.slice(0, mid) + '\u2026') <= maxWidth) lo = mid; else hi = mid - 1;
    }
    return (s.slice(0, lo).replace(/\s+$/, '') || s.slice(0, 1)) + '\u2026';
}

function drawBlock(doc, block, ctx) {
    const { margin, usable, ensure, setText } = ctx;
    let y = ctx.getY();

    if (block.kind === 'gauge') {
        ensure(70);
        doc.setFont('helvetica', 'bold'); doc.setFontSize(44);
        doc.setTextColor(229, 57, 53);
        doc.text(String(block.value), margin, y + 36);
        setText(12, 'bold', 30); doc.setTextColor(30);
        doc.text(block.band || '', margin + 80, y + 22);
        setText(9, 'normal', 120);
        doc.text(block.sub || '', margin + 80, y + 38);
        y += 56;
    } else if (block.kind === 'metrics') {
        const cols = 2;
        const cellW = usable / cols;
        const rows = Math.ceil(block.items.length / cols);
        ensure(rows * 48);
        block.items.forEach((m, i) => {
            const cx = margin + (i % cols) * cellW;
            const cy = y + Math.floor(i / cols) * 48;
            setText(18, 'bold', 20); doc.text(String(m.value), cx, cy + 18);
            setText(9, 'bold', 90); doc.text(m.label, cx, cy + 32);
            setText(8, 'normal', 140); doc.text(m.sub || '', cx, cy + 43);
        });
        y += rows * 48;
    } else if (block.kind === 'bars') {
        const max = Math.max(1, ...block.items.map((b) => (b.max != null ? b.max : Number(b.value) || 0)));
        ensure(block.items.length * 18 + 4);
        const labelW = 110, valW = 50, trackX = margin + labelW, trackW = usable - labelW - valW;
        block.items.forEach((b) => {
            setText(9, 'normal', 60); doc.text(clipText(doc, b.label, labelW - 6), margin, y + 9);
            doc.setFillColor(238, 242, 246); doc.rect(trackX, y + 2, trackW, 8, 'F');
            doc.setFillColor(0, 191, 165);
            const w = Math.max(1, Math.round((Number(b.value) || 0) / (b.max || max) * trackW));
            doc.rect(trackX, y + 2, w, 8, 'F');
            setText(9, 'bold', 40); doc.text(String(b.value), margin + usable, y + 9, { align: 'right' });
            y += 16;
        });
    } else if (block.kind === 'table') {
        const rows = block.rows.slice(0, DOC_TABLE_LIMIT);
        const ncol = block.columns.length;
        const colW = usable / ncol;
        ensure(20);
        setText(8, 'bold', 110);
        block.columns.forEach((c, i) => doc.text(String(c), margin + i * colW, y));
        y += 4; doc.setDrawColor(210); doc.setLineWidth(0.5); doc.line(margin, y, margin + usable, y); y += 10;
        setText(8.5, 'normal', 60);
        if (rows.length === 0) { setText(8.5, 'italic', 150); doc.text('(no rows)', margin, y); y += 13; setText(8.5, 'normal', 60); }
        rows.forEach((r) => {
            ensure(16);
            r.forEach((cell, i) => {
                doc.text(clipText(doc, cell, colW - 6), margin + i * colW, y);
            });
            y += 13;
            doc.setDrawColor(240); doc.line(margin, y - 4, margin + usable, y - 4);
        });
        if (block.total > rows.length) { setText(8, 'italic', 150); doc.text(`Showing ${rows.length} of ${block.total} rows · full table in Large data tables`, margin, y + 4); y += 12; }
    } else if (block.kind === 'narrative') {
        block.paragraphs.forEach((p) => {
            if (p.h) { ensure(20); setText(11, 'bold', 25); doc.text(doc.splitTextToSize(p.h, usable), margin, y); y += 15; }
            setText(9.5, 'normal', 70);
            const lines = doc.splitTextToSize(p.body, usable);
            ensure(lines.length * 12 + 4);
            doc.text(lines, margin, y); y += lines.length * 12 + 2;
            if (p.action) { setText(9, 'italic', 0); doc.setTextColor(0, 137, 123); const al = doc.splitTextToSize(`\u00BB ${p.action}`, usable); ensure(al.length * 12); doc.text(al, margin, y); y += al.length * 12 + 2; }
        });
    }
    ctx.setY(y);
}

// ==========================================================================
//  Document dispatch
// ==========================================================================
const FORMAT_EXT = { PDF: 'pdf', PowerPoint: 'html', Excel: 'xlsx', CSV: 'csv' };

// Single source of truth for a composed-document filename so the download and
// any user-facing label (e.g. the email-queued toast) can never drift from the
// real extension (PowerPoint -> .html, Excel -> .xlsx).
export function documentFilename(format, title) {
    const base = (title || 'export').replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
    return `${base}.${FORMAT_EXT[format] || 'pdf'}`;
}

export function generateDocument(format, title, sections) {
    const filename = documentFilename(format, title);
    if (format === 'CSV')        return { content: generateCsv(title, sections),  mime: 'text/csv;charset=utf-8',   filename };
    if (format === 'Excel')      return { content: generateXls(title, sections),  mime: XLSX_MIME,                  filename };
    if (format === 'PowerPoint') return { content: generatePptx(title, sections), mime: 'text/html;charset=utf-8',  filename };
    return { content: generatePdf(title, sections), mime: 'application/pdf', filename };
}

// ==========================================================================
//  Large-table exporters  (CSV / Excel / JSON / PDF at full row count)
// ==========================================================================
function tableCsv(columns, rows) {
    return [columns.map(escapeCsv).join(','), ...rows.map((r) => r.map(escapeCsv).join(','))].join('\n');
}

function tableXls(label, columns, rows) {
    return buildWorkbook([{ name: label, aoa: [columns, ...rows] }]);
}

function tableJson(columns, rows) {
    const keys = columns.map((c) => c.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''));
    return JSON.stringify(rows.map((r) => Object.fromEntries(keys.map((k, i) => [k, r[i]]))), null, 2);
}

function tablePdf(label, columns, rows) {
    const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'landscape' });
    const margin = 36;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usable = pageWidth - margin * 2;
    const colW = usable / columns.length;
    let y = margin;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.setTextColor(20);
    doc.text(label, margin, y); y += 16;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.setTextColor(130);
    doc.text(`${rows.length} rows · ${todayLabel()} · Boxphish`, margin, y); y += 16;

    const header = () => {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(110);
        columns.forEach((c, i) => doc.text(String(c), margin + i * colW, y));
        y += 4; doc.setDrawColor(200); doc.setLineWidth(0.5); doc.line(margin, y, margin + usable, y); y += 10;
        doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(60);
    };
    header();
    if (rows.length === 0) { doc.setFont('helvetica', 'italic'); doc.setTextColor(150); doc.text('(no rows)', margin, y); doc.setFont('helvetica', 'normal'); doc.setTextColor(60); y += 12; }
    rows.forEach((r) => {
        if (y > pageHeight - margin - 14) { doc.addPage(); y = margin; header(); }
        r.forEach((cell, i) => { doc.text(clipText(doc, cell, colW - 6), margin + i * colW, y); });
        y += 12; doc.setDrawColor(238); doc.line(margin, y - 4, margin + usable, y - 4);
    });
    const pageCount = doc.internal.getNumberOfPages();
    doc.setFontSize(8); doc.setTextColor(150);
    for (let p = 1; p <= pageCount; p++) { doc.setPage(p); doc.text(`Page ${p} of ${pageCount}`, pageWidth - margin, pageHeight - 16, { align: 'right' }); }
    return doc.output('blob');
}

export function generateTable(format, label, columns, rows) {
    const base = (label || 'table').replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (format === 'CSV')   return { content: tableCsv(columns, rows),        mime: 'text/csv;charset=utf-8',   filename: `${base}.csv` };
    if (format === 'Excel') return { content: tableXls(label, columns, rows), mime: XLSX_MIME,                  filename: `${base}.xlsx` };
    if (format === 'JSON')  return { content: tableJson(columns, rows),       mime: 'application/json',          filename: `${base}.json` };
    return { content: tablePdf(label, columns, rows), mime: 'application/pdf', filename: `${base}.pdf` };
}
